/* ============================================================
   LXPERFUME FX — cinematic effects engine
   WebGL molten-gold fluid · film grain · velocity cursor
   curtain page transitions · liquid image hover · video helpers
   ============================================================ */
window.FX = (function () {
  const $$ = (s, c) => Array.from((c || document).querySelectorAll(s));
  const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const touch = matchMedia("(hover: none), (pointer: coarse)").matches
    || ("ontouchstart" in window && (navigator.maxTouchPoints || 0) > 0);

  /* ================= WebGL molten gold ================= */
  const VERT = "attribute vec2 aPos;void main(){gl_Position=vec4(aPos,0.,1.);}";
  const FRAG = [
    "precision highp float;",
    "uniform vec2 uRes;uniform float uTime;uniform sampler2D uTrail;uniform float uAmp;",
    "float hash(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453123);}",
    "float noise(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);",
    " return mix(mix(hash(i),hash(i+vec2(1.,0.)),f.x),mix(hash(i+vec2(0.,1.)),hash(i+vec2(1.,1.)),f.x),f.y);}",
    "float fbm(vec2 p){float v=0.,a=.5;for(int i=0;i<5;i++){v+=a*noise(p);p*=2.03;a*=.5;}return v;}",
    "void main(){",
    " vec2 uv=gl_FragCoord.xy/uRes;",
    " vec2 p=uv*vec2(uRes.x/uRes.y,1.)*1.7;",
    " float t=uTime*.05;",
    " float trail=texture2D(uTrail,uv).r;",
    " vec2 q=vec2(fbm(p+t),fbm(p+vec2(5.2,1.3)-t*.8));",
    " vec2 r=vec2(fbm(p+2.*q+vec2(1.7,9.2)+t*.4+trail*1.4),fbm(p+2.*q+vec2(8.3,2.8)-t*.3));",
    " float f=fbm(p+2.2*r+trail*.9);",
    " vec3 black=vec3(.016,.014,.012);",
    " vec3 bronze=vec3(.30,.19,.075);",
    " vec3 gold=vec3(.82,.62,.30);",
    " vec3 champ=vec3(.99,.88,.60);",
    " vec3 col=mix(black,bronze,smoothstep(.28,.66,f)*uAmp);",
    " col=mix(col,gold,smoothstep(.55,.88,f)*(.22+.78*trail)*uAmp);",
    " col=mix(col,champ,smoothstep(.80,.99,f)*trail);",
    " col+=gold*trail*.30;",
    " float vig=1.0-smoothstep(.45,1.15,length(uv-.5));",
    " col*=vig;",
    " gl_FragColor=vec4(col,1.);}",
  ].join("\n");

  function goldFluid(canvas, opts) {
    opts = opts || {};
    const gl = canvas.getContext("webgl", { antialias: false, alpha: false });
    if (!gl) { canvas.classList.add("fx-fallback"); return null; }
    const prog = gl.createProgram();
    [[gl.VERTEX_SHADER, VERT], [gl.FRAGMENT_SHADER, FRAG]].forEach(([type, src]) => {
      const sh = gl.createShader(type);
      gl.shaderSource(sh, src); gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) console.error("FX shader:", gl.getShaderInfoLog(sh));
      gl.attachShader(prog, sh);
    });
    gl.linkProgram(prog); gl.useProgram(prog);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, "aPos");
    gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const uRes = gl.getUniformLocation(prog, "uRes");
    const uTime = gl.getUniformLocation(prog, "uTime");
    const uAmp = gl.getUniformLocation(prog, "uAmp");

    // mouse-trail heat texture (2d canvas, uploaded per frame)
    const tc = document.createElement("canvas"); tc.width = tc.height = 192;
    const tx = tc.getContext("2d");
    tx.fillStyle = "#000"; tx.fillRect(0, 0, 192, 192);
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    let mx = 0.5, my = 0.42, tmx = 0.5, tmy = 0.42, lastMove = 0, vel = 0;
    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      const nx = (e.clientX - r.left) / r.width, ny = (e.clientY - r.top) / r.height;
      vel = Math.min(1, Math.hypot(nx - tmx, ny - tmy) * 14);
      tmx = nx; tmy = ny; lastMove = performance.now();
    };
    window.addEventListener("mousemove", onMove, { passive: true });

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5) * 0.66;
      canvas.width = Math.max(2, canvas.clientWidth * dpr);
      canvas.height = Math.max(2, canvas.clientHeight * dpr);
      gl.viewport(0, 0, canvas.width, canvas.height);
    }
    resize(); window.addEventListener("resize", resize);

    let raf, visible = true, amp = 0;
    new IntersectionObserver((es) => { visible = es[0].isIntersecting; }, { threshold: 0 }).observe(canvas);

    function frame(now) {
      raf = requestAnimationFrame(frame);
      if (!visible) return;
      amp += (1 - amp) * 0.02; // fade the whole field in
      // idle: phantom droplet drifts in a lissajous when the mouse rests
      if (now - lastMove > 1800) {
        const k = now * 0.00028;
        tmx = 0.5 + Math.sin(k) * 0.31; tmy = 0.42 + Math.sin(k * 1.7 + 1.2) * 0.22; vel = 0.16;
      }
      mx += (tmx - mx) * 0.09; my += (tmy - my) * 0.09;
      // fade old heat, stamp new blob
      tx.globalCompositeOperation = "source-over";
      tx.fillStyle = "rgba(0,0,0,0.055)"; tx.fillRect(0, 0, 192, 192);
      const rad = 16 + vel * 30; vel *= 0.94;
      const g = tx.createRadialGradient(mx * 192, my * 192, 0, mx * 192, my * 192, rad);
      g.addColorStop(0, "rgba(255,255,255,0.55)"); g.addColorStop(1, "rgba(255,255,255,0)");
      tx.globalCompositeOperation = "lighter";
      tx.fillStyle = g;
      tx.beginPath(); tx.arc(mx * 192, my * 192, rad, 0, 7); tx.fill();
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, gl.LUMINANCE, gl.UNSIGNED_BYTE, tc);
      gl.uniform2f(uRes, canvas.width, canvas.height);
      gl.uniform1f(uTime, now * 0.001);
      gl.uniform1f(uAmp, opts.amp !== undefined ? opts.amp * amp : amp);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }
    raf = requestAnimationFrame(frame);
    return { destroy: () => { cancelAnimationFrame(raf); window.removeEventListener("mousemove", onMove); } };
  }

  /* ================= film grain ================= */
  function grain() {
    if (document.getElementById("fx-grain")) return;
    const c = document.createElement("canvas"); c.id = "fx-grain";
    c.width = 220; c.height = 220;
    const x = c.getContext("2d");
    const frames = [];
    const N = 6; // living grain on every device - the spectacle is the brand
    for (let f = 0; f < N; f++) {
      const d = x.createImageData(220, 220);
      for (let i = 0; i < d.data.length; i += 4) {
        const v = 200 + Math.random() * 55;
        d.data[i] = d.data[i + 1] = d.data[i + 2] = v;
        d.data[i + 3] = Math.random() * 28;
      }
      frames.push(d);
    }
    x.putImageData(frames[0], 0, 0);
    if (N > 1) {
      let fi = 0;
      setInterval(() => { if (document.hidden) return; x.putImageData(frames[fi = (fi + 1) % N], 0, 0); }, 90);
    }
    document.body.appendChild(c);
  }

  /* ================= velocity cursor ================= */
  function cursor() {
    return;   /* the pointer stays native: no dot, no ring, no lamp - the owner's call (2026-09-02) */
    if (touch) return;
    const blob = document.createElement("div"); blob.className = "fxc-blob";
    // stay invisible until a real mouse moves: some in-app WebViews misreport hover capability
    blob.style.visibility = "hidden";
    document.body.append(blob);
    let x = innerWidth / 2, y = innerHeight / 2, bx = x, by = y, dead = false;
    const onFirstMove = () => {
      if (dead) return;
      document.body.classList.add("fx-cursor-on");
      blob.style.visibility = "";
    };
    const onMove = (e) => { x = e.clientX; y = e.clientY; };
    const onOver = (e) => {
      const t = e.target.closest("a,button,.pcard,input,select,textarea,[data-cursor]");
      blob.classList.toggle("hot", !!t);
    };
    window.addEventListener("mousemove", onFirstMove, { once: true, passive: true });
    window.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseover", onOver);
    // a real touch means the media query lied (common in in-app WebViews) - tear it all down
    window.addEventListener("touchstart", () => {
      dead = true;
      window.removeEventListener("mousemove", onFirstMove);
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onOver);
      document.body.classList.remove("fx-cursor-on");
      blob.remove();
    }, { once: true, passive: true });
    (function loop() {
      if (dead) return;
      requestAnimationFrame(loop);
      const dx = x - bx, dy = y - by;
      bx += dx * 0.12; by += dy * 0.12;
      const v = Math.min(1, Math.hypot(dx, dy) / 120);
      const ang = Math.atan2(dy, dx) * 180 / Math.PI;
      blob.style.transform = `translate(${bx}px,${by}px) rotate(${ang}deg) scale(${1 + v * 0.55},${1 - v * 0.18})`;
    })();
  }

  /* ================= curtain page transitions ================= */
  function curtain() {
    const holder = document.createElement("div"); holder.className = "fx-curtain";
    for (let i = 0; i < 5; i++) { const s = document.createElement("i"); holder.appendChild(s); }
    document.body.appendChild(holder);
    const slats = $$("i", holder);
    // entrance (only after an internal navigation)
    if (sessionStorage.getItem("lx_nav") === "1") {
      sessionStorage.removeItem("lx_nav");
      holder.classList.add("cover");
      requestAnimationFrame(() => requestAnimationFrame(() => {
        slats.forEach((s, i) => { s.style.transitionDelay = (i * 0.055) + "s"; });
        holder.classList.add("lift");
        setTimeout(() => { holder.classList.remove("cover", "lift"); slats.forEach((s) => s.style.transitionDelay = ""); }, 1200);
      }));
    }
    // exit
    document.addEventListener("click", (e) => {
      if (e.target.closest("[data-add], button")) return; // in-card actions never navigate
      if (e.target.closest("[class*='jdgm']")) return; // Judge.me links open modals in place, never a page change
      const a = e.target.closest("a[href]");
      if (!a || e.metaKey || e.ctrlKey || a.target === "_blank") return;
      const href = a.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("http") || href.startsWith("mailto")) return;
      if (a.hash && a.pathname === location.pathname) return; // same-page anchor: just a scroll, no curtain
      e.preventDefault();
      sessionStorage.setItem("lx_nav", "1");
      slats.forEach((s, i) => { s.style.transitionDelay = (i * 0.055) + "s"; });
      holder.classList.add("drop");
      setTimeout(() => { location.href = href; }, 760);
    });
    // back/forward cache restore: the page comes back exactly as it left (curtain
    // deployed over everything) — lift it immediately or the page looks dead
    window.addEventListener("pageshow", (e) => {
      if (!e.persisted) return;
      slats.forEach((s) => { s.style.transitionDelay = ""; s.style.transition = "none"; });
      holder.classList.remove("drop", "cover", "lift");
      requestAnimationFrame(() => slats.forEach((s) => { s.style.transition = ""; }));
      sessionStorage.removeItem("lx_nav");
    });
  }

  /* ================= liquid image hover (SVG displacement) ================= */
  let liquidCount = 0;
  function liquidHover(scope) {
    if (touch) return;
    $$(".pcard .ph, [data-liquid]", scope || document).forEach((el) => {
      if (el.dataset.liquidOn) return; el.dataset.liquidOn = "1";
      const id = "fxliq" + (liquidCount++);
      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svg.setAttribute("class", "fx-liq-svg");
      svg.innerHTML = `<filter id="${id}"><feTurbulence type="fractalNoise" baseFrequency="0.012 0.02" numOctaves="2" result="n" seed="${liquidCount * 7}"/><feDisplacementMap in="SourceGraphic" in2="n" scale="0" xChannelSelector="R" yChannelSelector="G"/></filter>`;
      el.appendChild(svg);
      const disp = svg.querySelector("feDisplacementMap");
      const turb = svg.querySelector("feTurbulence");
      const img = el.querySelector("img, video");
      if (!img) return;
      let amt = 0, target = 0, raf = null, t0 = performance.now();
      function loop(now) {
        amt += (target - amt) * 0.08;
        turb.setAttribute("baseFrequency", `0.012 ${0.02 + Math.sin((now - t0) * 0.002) * 0.006}`);
        disp.setAttribute("scale", String(amt));
        if (amt > 0.4 || target > 0) { raf = requestAnimationFrame(loop); img.style.filter = `url(#${id})`; }
        else { img.style.filter = ""; raf = null; }
      }
      el.addEventListener("mouseenter", () => { target = 26; if (!raf) raf = requestAnimationFrame(loop); });
      el.addEventListener("mouseleave", () => { target = 0; });
    });
  }

  /* ================= char / word splitting ================= */
  function splitChars(el) {
    const walk = (node) => {
      Array.from(node.childNodes).forEach((n) => {
        if (n.nodeType === 3) {
          const frag = document.createDocumentFragment();
          n.textContent.split(/(\s+)/).forEach((word) => {
            if (!word) return;
            if (/^\s+$/.test(word)) { frag.appendChild(document.createTextNode(" ")); return; }
            const w = document.createElement("span");
            w.className = "fx-w";
            word.split("").forEach((ch) => {
              const s = document.createElement("span");
              s.className = "fx-ch"; s.textContent = ch;
              w.appendChild(s);
            });
            frag.appendChild(w);
          });
          node.replaceChild(frag, n);
        } else if (n.nodeType === 1 && !n.classList.contains("fx-ch") && !n.classList.contains("fx-w")) walk(n);
      });
    };
    walk(el);
    return $$(".fx-ch", el);
  }
  function heroTitle(el, delay) {
    const chs = splitChars(el);
    chs.forEach((c, i) => { c.style.transitionDelay = ((delay || 0) + i * 0.028) + "s"; });
    requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add("fx-title-in")));
  }
  function scrollTitle(el) {
    const chs = splitChars(el);
    chs.forEach((c, i) => { c.style.transitionDelay = (i * 0.02) + "s"; });
    new IntersectionObserver((es, io) => {
      if (es[0].isIntersecting) { el.classList.add("fx-title-in"); io.disconnect(); }
    }, { threshold: 0.35 }).observe(el);
  }

  /* ================= video helpers ================= */
  function makeVideo(src, opts) {
    opts = opts || {};
    const v = document.createElement("video");
    v.muted = true; v.loop = true; v.playsInline = true;
    v.setAttribute("muted", ""); v.setAttribute("playsinline", "");
    if (opts.preload) v.preload = opts.preload; // must be set before src so no bytes fetch early
    if (opts.poster) v.poster = opts.poster;
    v.src = src;
    if (opts.autoplay !== false) {
      new IntersectionObserver((es) => {
        es.forEach((e) => { if (e.isIntersecting) { v.play().catch(() => {}); } else v.pause(); });
      }, { threshold: 0.1 }).observe(v);
    }
    return v;
  }
  function hoverVideo(container, src, poster) {
    const v = makeVideo(src, { autoplay: false, poster, preload: "none" });
    v.className = "fx-hovervid";
    container.appendChild(v);
    const on = () => v.play().then(() => container.classList.add("vid-on")).catch(() => {});
    const off = () => { v.pause(); container.classList.remove("vid-on"); };
    if (touch) {
      // touch: hover never fires - run the film while the card is prominently in view, like a reel
      new IntersectionObserver((es) => {
        es.forEach((e) => { e.isIntersecting ? on() : off(); });
      }, { threshold: 0.55 }).observe(container);
      return;
    }
    container.addEventListener("mouseenter", on);
    container.addEventListener("mouseleave", off);
  }

  /* ================= magnetic elements ================= */
  function magnetic(scope) {
    if (touch) return;
    $$("[data-magnet]", scope || document).forEach((el) => {
      if (el.dataset.magOn) return; el.dataset.magOn = "1";
      const strength = parseFloat(el.dataset.magnet) || 0.35;
      el.addEventListener("mousemove", (e) => {
        const r = el.getBoundingClientRect();
        const dx = e.clientX - r.left - r.width / 2, dy = e.clientY - r.top - r.height / 2;
        el.style.transform = `translate(${dx * strength}px,${dy * strength}px)`;
      });
      el.addEventListener("mouseleave", () => {
        el.style.transition = "transform .7s cubic-bezier(.22,1,.36,1)";
        el.style.transform = "";
        setTimeout(() => el.style.transition = "", 700);
      });
    });
  }

  /* ================= list rows w/ floating preview ================= */
  function listPreview(rowsSelector, opts) {
    if (touch) return;
    const rows = $$(rowsSelector); if (!rows.length) return;
    const box = document.createElement("div"); box.className = "fx-preview";
    const imgA = document.createElement("img"); const imgB = document.createElement("img");
    box.append(imgA, imgB); document.body.appendChild(box);
    let cur = imgA, nxt = imgB, x = 0, y = 0, tx = 0, ty = 0, on = false;
    window.addEventListener("mousemove", (e) => { tx = e.clientX; ty = e.clientY; }, { passive: true });
    (function loop() {
      requestAnimationFrame(loop);
      x += (tx - x) * 0.12; y += (ty - y) * 0.12;
      box.style.transform = `translate(${x}px,${y}px) rotate(${(tx - x) * 0.05}deg)`;
    })();
    rows.forEach((row) => {
      row.addEventListener("mouseenter", () => {
        const src = row.dataset.img; if (!src) return;
        nxt.src = src; nxt.style.opacity = 1; cur.style.opacity = 0;
        const t = cur; cur = nxt; nxt = t;
        if (!on) { box.classList.add("on"); on = true; }
      });
      row.addEventListener("mouseleave", () => { box.classList.remove("on"); on = false; });
    });
  }

  /* ================= the honest ledger — 3D ingot sequence =================
     the fragrance ingot lands first, then the theater piles on top of it ->
     dollar figures + percentages count up -> the gold catches the light */
  // the bottle shown on the ledger's gold bar
  const LEDGER_BOTTLE = "https://cdn.shopify.com/s/files/1/0759/6428/5178/files/lx-bottle-hero.png?v=1784317448";
  function ledgerFX() {
    $$(".ledger").forEach((led) => {
      if (led.dataset.fxLedger) return; led.dataset.fxLedger = "1";
      const stacks = $$(".stack", led);
      const pcts = $$(".s-pct[data-pct]", led);
      $$(".slab.gold .s-face", led).forEach((f) => {
        const i = document.createElement("i"); i.className = "shine"; f.appendChild(i);
      });
      // the bottle lands on the solo gold bar (injected — no markup edits needed)
      const bottles = [];
      stacks.forEach((stack) => {
        const slabs = $$(".slab", stack);
        if (slabs.length === 1 && slabs[0].classList.contains("gold") && !stack.querySelector(".lx-bottle")) {
          const b = document.createElement("div");
          b.className = "lx-bottle";
          b.setAttribute("aria-hidden", "true");
          b.innerHTML = '<div class="b-core"><img src="' + LEDGER_BOTTLE + '" alt="" loading="lazy" decoding="async"><i class="b-sheen"></i></div><i class="b-shadow"></i>';
          stack.appendChild(b);
          bottles.push(b);
          if (!touch) { // desktop: the bottle leans toward the cursor
            const core = b.querySelector(".b-core");
            let tx = 0, ty = 0, cx = 0, cy = 0, raf = null;
            function loop() {
              raf = null;
              cx += (tx - cx) * 0.1; cy += (ty - cy) * 0.1;
              if (!b.classList.contains("tilt")) return;
              core.style.transform = "rotateY(" + cx.toFixed(2) + "deg) rotateX(" + cy.toFixed(2) + "deg)";
              raf = requestAnimationFrame(loop);
            }
            led.addEventListener("pointermove", (e) => {
              if (!b.classList.contains("on")) return;
              const r = b.getBoundingClientRect();
              tx = Math.max(-14, Math.min(14, (e.clientX - (r.left + r.width / 2)) / innerWidth * 52));
              ty = Math.max(-8, Math.min(8, ((r.top + r.height / 2) - e.clientY) / innerHeight * 26));
              b.classList.add("tilt");
              if (!raf) raf = requestAnimationFrame(loop);
            });
            led.addEventListener("pointerleave", () => {
              b.classList.remove("tilt");
              core.style.transform = ""; cx = cy = 0;
            });
          }
        }
      });
      const tags = $$(".bar-tag", led).map((tag) => {
        const node = tag.firstChild;
        const m = node && node.nodeType === 3 && node.textContent.match(/^([^\d]*)([\d,]+(?:\.\d+)?)(.*)$/);
        if (!m) return null;
        return { node, pre: m[1], val: parseFloat(m[2].replace(/,/g, "")), dec: (m[2].split(".")[1] || "").length, post: m[3] };
      }).filter(Boolean);
      // the light beam + gold dust layers
      led.insertAdjacentHTML("beforeend",
        '<i class="beam" aria-hidden="true"></i>' +
        '<span class="dust" aria-hidden="true"><i></i><i></i><i></i><i></i><i></i><i></i><i></i></span>');
      // desktop: living material — gold reflections pan with the cursor, the scene tilts
      if (!touch) {
        let tuned = false;
        const goldFaces = $$(".slab.gold .s-face", led);
        led.addEventListener("pointermove", (e) => {
          const r = led.getBoundingClientRect();
          const mx = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
          const my = Math.max(0, Math.min(1, (e.clientY - r.top) / r.height));
          goldFaces.forEach((f) => { f.style.backgroundPosition = (20 + mx * 60).toFixed(1) + "% 0"; });
          if (!led.classList.contains("in")) return;
          if (!tuned) { led.style.transition = "transform 0.3s ease-out"; tuned = true; }
          led.style.transform = "perspective(1300px) rotateY(" + ((mx - 0.5) * 2.4).toFixed(2) + "deg) rotateX(" + ((0.5 - my) * 1.6).toFixed(2) + "deg)";
        });
        led.addEventListener("pointerleave", () => {
          goldFaces.forEach((f) => { f.style.backgroundPosition = ""; });
          led.style.transform = "";
        });
        // hovering "our bottle" deletes the theater layers on the left
        const usCol = led.querySelector(".col:nth-of-type(2)");
        if (usCol) {
          usCol.addEventListener("pointerenter", () => led.classList.add("focus-us"));
          usCol.addEventListener("pointerleave", () => led.classList.remove("focus-us"));
        }
      }
      const shine = () => $$(".slab.gold", led).forEach((s) => s.classList.add("shine-on"));
      const bottleOn = () => bottles.forEach((b) => b.classList.add("on"));
      const tickPct = (el) => {
        const target = parseFloat(el.dataset.pct), t0 = performance.now(), DUR = 700;
        (function step(now) {
          const k = Math.min(1, (now - t0) / DUR);
          el.textContent = "~" + Math.round(target * (1 - Math.pow(1 - k, 3))) + "%";
          if (k < 1) requestAnimationFrame(step);
        })(t0);
      };
      const finals = () => {
        $$(".slab", led).forEach((s) => s.classList.add("land"));
        pcts.forEach((p) => { p.textContent = "~" + p.dataset.pct + "%"; });
        shine();
        bottleOn();
      };
      pcts.forEach((p) => { p.textContent = "~0%"; });
      new IntersectionObserver((es, io) => {
        if (!es[0].isIntersecting) return;
        io.disconnect();
        // stack each column bottom-up: the fragrance lands, the theater piles on —
        // each layer's percentage ticks up the moment its ingot touches down
        let landAt = 0;
        stacks.forEach((stack, ci) => {
          const slabs = $$(".slab", stack).reverse();
          slabs.forEach((s, i) => {
            const t = ci * 750 + i * 230;
            landAt = Math.max(landAt, t);
            setTimeout(() => {
              s.classList.add("land");
              const pc = s.querySelector(".s-pct[data-pct]");
              if (pc) tickPct(pc);
            }, t);
          });
        });
        const t0 = performance.now(), DUR = 1500, DELAY = 550;
        (function tick(now) {
          const k = Math.min(1, Math.max(0, (now - t0 - DELAY) / DUR));
          const e = 1 - Math.pow(1 - k, 3); // easeOutCubic
          tags.forEach((t) => {
            const v = t.val * e;
            t.node.textContent = t.pre + (t.dec ? v.toFixed(t.dec) : Math.round(v).toLocaleString()) + t.post;
          });
          if (k < 1) requestAnimationFrame(tick);
        })(t0);
        setTimeout(bottleOn, landAt + 550);
        setTimeout(shine, landAt + 900);
        setTimeout(() => led.classList.add("lit"), landAt + 800);
      }, { threshold: 0.35 }).observe(led);
    });
  }

  function init() {
    grain();
    cursor();
    curtain();
    ledgerFX();
  }

  return { goldFluid, grain, cursor, curtain, liquidHover, splitChars, heroTitle, scrollTitle, makeVideo, hoverVideo, magnetic, listPreview, ledgerFX, init, reduced, touch };
})();
